"use client";

import React, { useEffect, useRef, useState } from "react";

import Button from "../library/Button";
import Image from "next/image";
import Link from "next/link";
import Logo from "../Logo";
import MenuIcon from "../icons/MenuIcon";
import clsx from "clsx";
import { usePathname } from "next/navigation";
import { useTranslation } from "react-i18next";

// Simple CloseIcon component
const CloseIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 6L6 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6 6L18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Custom NavLink component
type NavLinkProps = {
  href: string;
  active?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
};

const NavLink: React.FC<NavLinkProps> = ({
  href,
  active,
  children,
  onClick,
  className,
}) => {
  return (
    <Link
      href={href}
      className={clsx(
        "transition-colors",
        "hover:text-gail-primary",
        active ? "text-gail-primary" : "text-gray-800",
        className
      )}
      onClick={onClick}
    >
      {children}
    </Link>
  );
};

// Desktop Navigation component
type DesktopNavigationProps = {
  pathname: string | null;
};

const DesktopNavigation: React.FC<DesktopNavigationProps> = ({ pathname }) => {
  const { t } = useTranslation();

  return (
    <div className="hidden md:flex items-center">
      <div className="mr-12 flex space-x-8">
        <NavLink href="/product" active={pathname?.startsWith("/product")}>
          {t("header.product")}
        </NavLink>
        <NavLink href="/pricing" active={pathname?.startsWith("/pricing")}>
          {t("header.pricing")}
        </NavLink>
        <NavLink
          href="/compliance"
          active={pathname?.startsWith("/compliance")}
        >
          {t("header.compliance")}
        </NavLink>
        <NavLink href={process.env.NEXT_PUBLIC_GAIL_DASHBOARD_URL ?? "#"}>
          {t("header.signIn")}
        </NavLink>
      </div>
      <Button href={`/contact-sales`} className="text-sm">
        {t("header.requestDemo")}
      </Button>
    </div>
  );
};

// Mobile Menu component
type MobileMenuProps = {
  isOpen: boolean;
  toggleMenu: () => void;
  pathname: string | null;
};

const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  toggleMenu,
  pathname,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={clsx(
        "md:hidden",
        "transition-all duration-300",
        "w-full h-screen",
        "fixed inset-0",
        "bg-[#F9F3E6]",
        "z-50",
        "flex flex-col",
        "px-2 sm:px-4",
        isOpen
          ? "opacity-100 pointer-events-auto"
          : "opacity-0 pointer-events-none"
      )}
    >
      <div className="flex justify-between py-4 pt-[17px]">
        <Link href="/" className="flex-shrink-0" onClick={() => toggleMenu()}>
          <Logo />
        </Link>
        <button
          onClick={toggleMenu}
          className="text-black transition-all duration-300"
          aria-label="Close menu"
        >
          <CloseIcon />
        </button>
      </div>

      <div className="px-2 flex flex-col justify-between h-full">
        <div className="flex flex-col font-semibold text-[40px] mt-4">
          <NavLink
            href="/product"
            active={pathname?.startsWith("/product")}
            onClick={toggleMenu}
            className="border-b border-black/20"
          >
            {t("header.product")}
          </NavLink>
          <NavLink
            href="/pricing"
            active={pathname?.startsWith("/pricing")}
            onClick={toggleMenu}
            className="border-b border-black/20"
          >
            {t("header.pricing")}
          </NavLink>
          <NavLink
            href="/compliance"
            active={pathname?.startsWith("/compliance")}
            onClick={toggleMenu}
            className="border-b border-black/20"
          >
            {t("header.compliance")}
          </NavLink>
          <NavLink
            href={process.env.NEXT_PUBLIC_GAIL_DASHBOARD_URL ?? "#"}
            onClick={toggleMenu}
          >
            {t("header.signIn")}
          </NavLink>
        </div>

        <div className="flex-grow flex items-center justify-center max-h-[38vh] py-4">
          <Image
            src="/images/mobile_nav.webp"
            alt="Gail Menu Graphic"
            width={500}
            height={500}
            className="w-auto h-full max-h-full object-contain"
          />
        </div>

        <Button
          type="tertiary"
          href={`/contact-sales`}
          className="w-full text-base font-normal border border-black py-3 mb-8"
        >
          {t("header.requestDemo")}
        </Button>
      </div>
    </div>
  );
};

// Main Header component
const Header: React.FC = () => {
  const [show, setShow] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const lastScroll = useRef(0);
  const ticking = useRef(false);
  const pathname = usePathname();

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    const threshold = 16;
    const onScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          const y = window.scrollY;
          if (y < threshold) {
            setShow(true);
            lastScroll.current = y;
            ticking.current = false;
            return;
          }
          if (Math.abs(y - lastScroll.current) < threshold) {
            ticking.current = false;
            return;
          }
          if (y < lastScroll.current) setShow(true);
          else if (y > lastScroll.current) setShow(false);
          lastScroll.current = y;
          ticking.current = false;
        });
        ticking.current = true;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className={clsx(
          "sticky",
          "top-0",
          "z-40",
          "w-full",
          "bg-white",
          "transition-transform",
          "duration-300",
          "will-change-transform",
          "font-monument",
          "px-4",
          show ? "translate-y-0" : "-translate-y-full"
        )}
      >
        <nav
          className={clsx(
            "max-w-8xl",
            "mx-auto",
            "z-60",
            "py-4",
            "px-2 md:px-0"
          )}
        >
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link href="/" className="flex-shrink-0">
              <Logo />
            </Link>

            {/* Desktop Navigation */}
            <DesktopNavigation pathname={pathname} />

            {/* Mobile Navigation Toggle */}
            <div className="flex items-center md:hidden">
              <button
                onClick={toggleMenu}
                className="text-black transition-all duration-300"
                aria-label="Toggle menu"
              >
                <MenuIcon />
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          <MobileMenu
            isOpen={isOpen}
            toggleMenu={toggleMenu}
            pathname={pathname}
          />
        </nav>
      </header>
    </>
  );
};

export default Header;
